import React, { useState } from 'react';
import { DispatchAsyncResult } from 'react-redux-dispatch-async/lib/dispatchAsync';
import { DocColor, StyleSheet } from '../../../../assets/styles/constantStyles';
import { User } from '../../../../authContext/domain/entities/User';
import { useNotificationContext } from '../../../../common/configuration/notificationContextProvider';
import { ToggleButton } from '../../../../common/ui/ToggleButton';
import { Rpx } from '../../../../common/usecases/helpers/DimensionsConverter';
import { UserSettings } from '../../../domain/entities/UserSettings';

interface NotificationsActionsComponentProps {
  userSettingsState: UserSettings;
  loadUserSettingsAsync(): Promise<DispatchAsyncResult<User>>;
  updateUserSettingsAsync(userSettings: UserSettings): Promise<DispatchAsyncResult<UserSettings>>;
  setChosenOption: React.Dispatch<React.SetStateAction<string>>;
}

export const NotificationsActionsComponent = ({
  userSettingsState,
  loadUserSettingsAsync,
  updateUserSettingsAsync,
  setChosenOption,
}: NotificationsActionsComponentProps) => {
  const notification = useNotificationContext();
  const [userSettingsUseState, setUserSettingsUseState] = useState<UserSettings>(userSettingsState);

  const onSubmitClickHandler = () => {
    if (JSON.stringify(userSettingsUseState) !== JSON.stringify(userSettingsState)) {
      updateUserSettingsAsync(userSettingsUseState).then((res) => {
        if (res.success) {
          loadUserSettingsAsync();
          setChosenOption('');
          notification.setOnSuccess({
            message: `Votre notifications d’action vient d’être modifié.
          `,
          });
        } else {
          notification.setOnError({
            message: `Nous n'avons pas pu mettre à jour votre notifications d’action.
          `,
          });
        }
      });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.titleDiv}>
        <p style={{ ...styles.titleParagraph, fontSize: Rpx(23, true) }}>Notifications d’action</p>
      </div>
      <div style={{ margin: 'auto', alignItems: 'center' }}>
        <p style={{ ...styles.explanationParagraph, fontSize: Rpx(25, true) }}>
          Afficher les notifications sur votre moniteur lors de l’enregistrement de nouvelles données où bien en cas de
          modifications.
        </p>
        <div style={styles.horizontalLine} />
        <div style={styles.notificationsActionsContainer}>
          <div
            style={styles.notificationsActionsStateContainer}
            onKeyDown={() => {
              setUserSettingsUseState((prevData) => ({
                ...prevData,
                notificationsActionsState: !prevData.notificationsActionsState,
              }));
            }}
            tabIndex={0}
            role="button"
            onClick={() => {
              setUserSettingsUseState((prevData) => ({
                ...prevData,
                notificationsActionsState: !prevData.notificationsActionsState,
              }));
            }}
          >
            <p style={{ ...styles.notificationsActionsStateTitle, fontSize: Rpx(28, true) }}>
              Activer les notifications d’action
            </p>
            <ToggleButton selected={userSettingsUseState.notificationsActionsState} width={Rpx(40)} height={Rpx(18)} />
          </div>
        </div>
        <div style={styles.notificationsActionsContainer}>
          <p style={{ ...styles.notificationsActionsDelaiTitle, fontSize: Rpx(24, true) }}>
            Modifier le temps de visibilité de la notification
          </p>

          <div
            style={styles.notificationsActionsDelaiContainer}
            onKeyDown={() => {
              setUserSettingsUseState((prevData) => ({
                ...prevData,
                notificationsActionsDelai: prevData.notificationsActionsDelai === 5000 ? 10000 : 5000,
              }));
            }}
            tabIndex={0}
            role="button"
            onClick={() => {
              setUserSettingsUseState((prevData) => ({
                ...prevData,
                notificationsActionsDelai: prevData.notificationsActionsDelai === 5000 ? 10000 : 5000,
              }));
            }}
          >
            <ToggleButton
              selected={userSettingsUseState.notificationsActionsDelai === 5000}
              width={Rpx(40, true)}
              height={Rpx(18, true)}
            />
            <p style={{ ...styles.notificationsActionsDelaiLabel, fontSize: Rpx(25, true) }}>5 secondes</p>
          </div>
          <div
            style={styles.notificationsActionsDelaiContainer}
            onKeyDown={() => {
              setUserSettingsUseState((prevData) => ({
                ...prevData,
                notificationsActionsDelai: prevData.notificationsActionsDelai === 5000 ? 10000 : 5000,
              }));
            }}
            tabIndex={0}
            role="button"
            onClick={() => {
              setUserSettingsUseState((prevData) => ({
                ...prevData,
                notificationsActionsDelai: prevData.notificationsActionsDelai === 5000 ? 10000 : 5000,
              }));
            }}
          >
            <ToggleButton
              selected={userSettingsUseState.notificationsActionsDelai === 10000}
              width={Rpx(40)}
              height={Rpx(18)}
            />
            <p style={{ ...styles.notificationsActionsDelaiLabel, fontSize: Rpx(25, true) }}>10 secondes</p>
          </div>
        </div>
        <div style={styles.notificationsActionsButtonsContainer}>
          <button
            style={{
              ...styles.button,
              height: Rpx(70, true),
              fontSize: Rpx(28, true),
              background:
                JSON.stringify(userSettingsUseState) === JSON.stringify(userSettingsState)
                  ? DocColor.GRAY
                  : DocColor.MEDIUMBLUE,
            }}
            type="button"
            disabled={JSON.stringify(userSettingsUseState) === JSON.stringify(userSettingsState)}
            onClick={onSubmitClickHandler}
          >
            Valider
          </button>
          <button
            style={{ ...styles.button, height: Rpx(70, true), fontSize: Rpx(28, true) }}
            type="button"
            onClick={() => setChosenOption('')}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: StyleSheet = {
  container: {
    position: 'relative',
    width: '52.5%',
    marginLeft: '5%',
    outline: `${Rpx(10)} solid ${DocColor.DARKBLUE}`,
    borderRadius: Rpx(60),
    height: '100%',
    overflow: 'hidden',
  },
  titleDiv: {
    width: 'fit-content',
    background: DocColor.DARKBLUE,
    borderRadius: `0 0 ${Rpx(15)} ${Rpx(15)}`,
    minHeight: Rpx(45),
    padding: `0 ${Rpx(40)}`,
    margin: `0 auto`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleParagraph: {
    color: 'white',
    fontWeight: 600,
    marginBottom: Rpx(10),
  },
  explanationParagraph: {
    color: DocColor.DARKBLUE,
    fontWeight: 700,
    padding: `0 ${Rpx(32)}`,
    marginTop: Rpx(40),
    textAlign: 'center',
  },
  horizontalLine: {
    width: '80%',
    background: DocColor.MEDIUMBLUE,
    height: Rpx(8),
    borderRadius: Rpx(4),
    marginTop: Rpx(30),
  },
  notificationsActionsContainer: {
    width: '90%',
    minHeight: Rpx(50),
    background: DocColor.CLOUDYBLUE,
    margin: `${Rpx(40)} 5% ${Rpx(20)} 5%`,
    padding: `${Rpx(30)} 0`,
    borderRadius: Rpx(30),
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationsActionsStateContainer: {
    cursor: 'pointer',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Rpx(12),
  },
  notificationsActionsStateTitle: {
    color: DocColor.DARKBLUE,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  notificationsActionsDelaiContainer: {
    cursor: 'pointer',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Rpx(20),
    gap: Rpx(15),
  },
  notificationsActionsDelaiTitle: {
    color: DocColor.DARKBLUE,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  notificationsActionsDelaiLabel: {
    color: DocColor.DARKBLUE,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  notificationsActionsButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '65%',
    margin: `${Rpx(20)} auto`,
  },
  button: {
    width: 'fit-content',
    backgroundColor: DocColor.DARKBLUE,
    color: 'white',
    fontWeight: 700,
    borderRadius: Rpx(12),
    flexDirection: 'row',
    padding: `0px ${Rpx(40)}`,
  },
};
